
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CalendarDays, ArrowRight, Users } from "lucide-react";
import BookingCalendar from "./BookingCalendar";
import WaitlistForm from "./WaitlistForm";

const BookingWidget = () => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState<Date | undefined>(undefined);
  const [checkOut, setCheckOut] = useState<Date | undefined>(undefined);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);

  const handleDatesSelected = (dates: { checkIn: Date | undefined; checkOut: Date | undefined }) => {
    setCheckIn(dates.checkIn);
    setCheckOut(dates.checkOut);
  };

  const handleBookNow = () => {
    if (checkIn && checkOut) {
      const params = new URLSearchParams({
        checkIn: checkIn.toISOString(),
        checkOut: checkOut.toISOString(),
      });
      navigate(`/reserver?${params.toString()}`);
    } else {
      navigate("/reserver");
    }
  };

  const nights =
    checkIn && checkOut
      ? Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
      : null;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="bg-villa-blue px-6 py-5">
        <h3 className="font-playfair text-white font-bold text-xl mb-0.5">Réservez votre séjour</h3>
        <p className="text-white/50 text-sm">Disponibilité en temps réel</p>
      </div>

      {/* Body */}
      <div className="px-6 py-6 flex flex-col gap-4">
        {/* Date picker trigger */}
        <div>
          <label className="block text-xs font-medium text-villa-text-secondary mb-2 tracking-wide uppercase">
            Vos dates
          </label>
          <Dialog>
            <DialogTrigger asChild>
              <button className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-black/[0.08] bg-villa-offwhite hover:bg-black/[0.03] transition-colors text-left group">
                <CalendarDays className="h-5 w-5 text-villa-turquoise flex-shrink-0" strokeWidth={1.5} />
                <span className={`text-sm flex-1 ${checkIn && checkOut ? "text-villa-text font-medium" : "text-villa-text-secondary"}`}>
                  {checkIn && checkOut
                    ? `${format(checkIn, "d MMM", { locale: fr })} → ${format(checkOut, "d MMM yyyy", { locale: fr })}`
                    : "Choisir vos dates d'arrivée et départ"}
                </span>
                <ArrowRight className="h-4 w-4 text-villa-text-secondary group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] rounded-3xl border-0 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-playfair text-xl">Calendrier de disponibilité</DialogTitle>
                <DialogDescription className="text-villa-text-secondary">
                  Sélectionnez vos dates d'arrivée et de départ.
                </DialogDescription>
              </DialogHeader>
              <BookingCalendar onDatesSelected={handleDatesSelected} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Nights summary */}
        {nights && nights > 0 && (
          <div className="flex items-center justify-between px-4 py-3 bg-villa-turquoise/8 rounded-2xl border border-villa-turquoise/15">
            <span className="text-sm text-villa-text-secondary">Durée du séjour</span>
            <span className="text-sm font-semibold text-villa-turquoise">{nights} nuit{nights > 1 ? "s" : ""}</span>
          </div>
        )}

        {/* Capacity hint */}
        <div className="flex items-center gap-2 text-xs text-villa-text-secondary">
          <Users className="h-4 w-4" strokeWidth={1.5} />
          <span>Jusqu'à 8 personnes</span>
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-2.5 pt-1">
          <Button
            onClick={handleBookNow}
            className="w-full h-12 rounded-2xl bg-villa-turquoise hover:bg-villa-turquoise/90 text-white font-medium text-[15px] transition-all duration-300 hover:shadow-lg hover:shadow-villa-turquoise/20"
          >
            Réserver maintenant
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowWaitlistDialog(true)}
            className="w-full h-10 rounded-2xl text-villa-text-secondary text-sm hover:text-villa-text hover:bg-black/[0.04]"
          >
            Rejoindre la liste d'attente
          </Button>
        </div>
      </div>

      {/* Waitlist dialog */}
      <Dialog open={showWaitlistDialog} onOpenChange={setShowWaitlistDialog}>
        <DialogContent className="sm:max-w-[600px] rounded-3xl border-0 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-playfair text-xl">Rejoindre la liste d'attente</DialogTitle>
            <DialogDescription className="text-villa-text-secondary">
              Laissez-nous vos coordonnées et nous vous contacterons en priorité si des dates se libèrent.
            </DialogDescription>
          </DialogHeader>
          <WaitlistForm checkIn={checkIn} checkOut={checkOut} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingWidget;
